'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';

import { CategoryIcon } from '@/components/CategoryIcon';
import { buildSubcategoryRowsFromTxs } from '@/components/transactions/category-detail/buildCategoryRows';
import { buildCategoryIndexFromServerMeta } from '@/components/transactions/screen/categoryIndex';
import { adaptTransactionForScreen } from '@/components/transactions/screen/TransactionsScreen';
import { useCategoryOverview } from '@/hooks/queries/useCategoryOverview';
import { TRANSACTIONS_MAX_PAGE, useTransactionsPage } from '@/hooks/queries/useTransactionsPage';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { trackEvent } from '@/lib/telemetry';
import { categoryChipStyleFromColor } from '@/lib/transactions/categories';
import { cn } from '@/lib/utils';
import {
  useHomeTopCategories,
  type TopCategoriesData,
  type TopCategoryRow,
} from '@/hooks/queries/useHomeTopCategories';
import { WidgetCard, widgetState } from './WidgetCard';

/**
 * W7 · Top 5 categorias — the cycle's biggest spend categories as bars whose
 * width is each category's share of TOTAL cycle spend (so the bars read against
 * "100% of what I spent"), with the share % + amount and the cycle's expense total.
 * Clicking a row expands the subcategory drill-down inline — the same single-open
 * behavior (and the same aggregation) as the Categorias page's caret.
 */
export function CategoriasWidget(): ReactNode {
  const { data, error, isLoading } = useHomeTopCategories();

  return (
    <WidgetCard
      eyebrow="Top 5 categorias"
      action={{ label: 'Categorias', href: '/budget' }}
      state={widgetState(data, error, isLoading, isCategoriesEmpty)}
    >
      <TopCategoriesBody data={data} />
    </WidgetCard>
  );
}

function isCategoriesEmpty(data: TopCategoriesData): boolean {
  return data.rows.length === 0;
}

function TopCategoriesBody({ data }: { data: TopCategoriesData | undefined }): ReactNode {
  const formatCurrency = useFormatCurrency();
  // Single-open drill-down, like the Categorias page: expanding one collapses the other.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Budgeted categories overlay their limit on the bar — same monthly scope as
  // the widget's own meta query.
  const now = new Date();
  const { data: overview } = useCategoryOverview({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });
  const limitByRef = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of overview?.rows ?? []) {
      if (row.limit != null && row.limit > 0) map.set(row.categoryRef, row.limit);
    }
    return map;
  }, [overview]);

  if (!data || data.rows.length === 0) return null;

  const toggle = (id: string): void => {
    setExpandedId((current) => (current === id ? null : id));
    trackEvent('new_home.widget_action', { widget: 'categorias', action: 'toggle_category' });
  };

  return (
    <div className="flex flex-col">
      <ul className="space-y-3">
        {data.rows.map((row) => (
          <CategoryRow
            key={row.id}
            row={row}
            limit={limitByRef.get(row.id) ?? null}
            cycleTotal={data.total}
            expanded={expandedId === row.id}
            onToggle={() => toggle(row.id)}
          />
        ))}
      </ul>
      <footer className="mt-4 flex items-center justify-between gap-3 border-t border-border-subtle pt-3">
        <span className="text-xs text-text-secondary">Total no ciclo</span>
        <span className="font-mono text-sm font-semibold text-text-primary tabular-nums">
          {formatCurrency(data.total, { noCents: true })}
        </span>
      </footer>
    </div>
  );
}

function CategoryRow({
  row,
  limit,
  cycleTotal,
  expanded,
  onToggle,
}: {
  row: TopCategoryRow;
  limit: number | null;
  cycleTotal: number;
  expanded: boolean;
  onToggle: () => void;
}): ReactNode {
  const formatCurrency = useFormatCurrency();
  const chip = categoryChipStyleFromColor(row.color);
  const shareRounded = Math.round(row.ratio * 100);
  // Never print "0%" for a category that did spend — show "<1%" instead.
  const shareLabel = row.ratio > 0 && shareRounded === 0 ? '<1%' : `${shareRounded}%`;
  // Floor the BAR at 2% so the smallest category never collapses into nothing.
  const barWidth = `${Math.max(2, shareRounded)}%`;
  // Budgeted category: the outline marks the limit on the SAME scale as the
  // bars (share of total cycle spend), so the fill crossing it reads as
  // "over budget". A limit larger than everything spent clamps to the track.
  const limitWidth =
    limit != null && cycleTotal > 0
      ? `${Math.min(100, Math.max(2, Math.round((limit / cycleTotal) * 100)))}%`
      : null;

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Recolher' : 'Expandir'} ${row.label}`}
        className="flex w-full cursor-pointer items-center gap-3 text-left transition-opacity hover:opacity-80"
      >
        <span
          className="flex size-9 shrink-0 items-center justify-center rounded-2xl"
          style={{ backgroundColor: chip.backgroundColor }}
          aria-hidden
        >
          <CategoryIcon name={row.iconName} className="size-[18px]" style={{ color: chip.color }} />
        </span>
        <span className="flex w-24 shrink-0 items-center gap-1 sm:w-32">
          <span className="truncate text-sm font-semibold text-text-primary">{row.label}</span>
          <CaretDownIcon
            size={11}
            weight="bold"
            aria-hidden
            className={cn(
              'shrink-0 text-text-tertiary transition-transform',
              expanded && 'rotate-180',
            )}
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="self-end font-mono text-2xs leading-none text-text-tertiary tabular-nums">
            {shareLabel}
          </span>
          <span className="relative h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
            <span
              data-testid="category-bar"
              className="block h-full rounded-full"
              style={{ width: barWidth, backgroundColor: row.color }}
            />
            {limitWidth && (
              <span
                data-testid="category-limit"
                aria-hidden
                title={limit != null ? `Limite ${formatCurrency(limit, { noCents: true })}` : undefined}
                className="absolute inset-y-0 left-0 rounded-full border"
                style={{ width: limitWidth, borderColor: row.color }}
              />
            )}
          </span>
        </span>
        <span className="w-20 shrink-0 text-right font-mono text-sm font-semibold text-text-primary tabular-nums">
          {formatCurrency(row.amount, { noCents: true })}
        </span>
      </button>
      {expanded && <CategoryChildren categoryId={row.id} />}
    </li>
  );
}

/**
 * The Categorias page's drill-down, read-only: leaf spend aggregated from the
 * cycle's transactions (server facets are parent-level only, so leaves come
 * from the transactions themselves — same approach as the page and the detail
 * drawer). Fetches only while expanded.
 */
function CategoryChildren({ categoryId }: { categoryId: string }): ReactNode {
  const formatCurrency = useFormatCurrency();
  // The widget's cycle window: the current calendar month in UTC — the bar
  // amounts come from /transactions/meta resolved server-side by {year, month}
  // over UTC reference dates, so the drill-down must slice the same way or
  // month-boundary rows make the children visibly disagree with their bar.
  const { from, to } = useMemo(() => {
    const now = new Date();
    return {
      from: new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)),
      to: new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)),
    };
  }, []);
  const { data: page, isLoading } = useTransactionsPage({
    from,
    to,
    parentCategoryId: categoryId,
    limit: TRANSACTIONS_MAX_PAGE,
  });

  const children = useMemo(() => {
    const txs = (page?.items ?? []).map(adaptTransactionForScreen);
    const index = buildCategoryIndexFromServerMeta(undefined, txs);
    return buildSubcategoryRowsFromTxs(txs, categoryId, index);
  }, [page, categoryId]);

  if (isLoading && !page) {
    return (
      <div className="mt-2 space-y-1.5 pl-12">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-6 animate-pulse rounded-lg bg-surface-sunken" />
        ))}
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <p className="mt-2 pl-12 text-2xs text-text-tertiary">
        Sem subcategorias com movimento no período.
      </p>
    );
  }

  return (
    <ul className="mt-2 space-y-1 pl-12" role="list">
      {children.map((child) => (
        <li key={child.id} className="flex items-center gap-2.5">
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-xs"
            style={{ backgroundColor: child.color }}
          />
          <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{child.label}</span>
          <span className="hidden font-mono text-2xs text-text-tertiary tabular-nums sm:inline">
            {child.count} {child.count === 1 ? 'lançamento' : 'lançamentos'}
          </span>
          <span className="font-mono text-sm font-medium text-text-primary tabular-nums">
            {formatCurrency(child.amount)}
          </span>
          <span className="w-10 shrink-0 text-right font-mono text-2xs text-text-tertiary tabular-nums">
            {child.pct.toFixed(child.pct < 10 ? 1 : 0)}%
          </span>
        </li>
      ))}
    </ul>
  );
}
